import React, { useState, useEffect } from "react";
import DropdownSelect from "../../../components/generic/DropdownSelect";
import { Col, Input, Row, Select, Tooltip, InputNumber } from "antd";
import { QuestionCircleOutlined, CodeOutlined } from "@ant-design/icons";
import { getAllConnection } from "../../../service/db.service";

const { Option } = Select;
const { TextArea } = Input;

const eventTypeList = [
  {
    label: "File received in a location",
    value: "File received in a location",
  },
  { label: "SQL Event", value: "SQL Event" },
];

function EventMonitorTask(props) {
  const [eventType, setEventType] = useState("");
  const [fileName, setFileName] = useState("");
  const [matchType, setMatchType] = useState("");
  const [matchDisabled, setMatchDisabled] = useState(false);
  const [environmentList, setEnvironmentList] = useState([]);
  const [dbList, setDbList] = useState([]);
  const [allConnections, setAllConnections] = useState([]);
  const [environmentName, setEnvironmentName] = useState("");
  const [dbConnection, setDbConnection] = useState("");
  const [sqlQuery, setSqlQuery] = useState("");
  const [pollInterval, setPollInterval] = useState(10);
  const [pollTimeout, setPollTimeout] = useState(300);

  // Load all DB connections on mount
  useEffect(() => {
    getAllConnection().then((res) => {
      setAllConnections(res.data || []);
      const uniqueEnvs = Array.from(
        new Set((res.data || []).map((i) => i.environmentName)),
      ).map((e) => ({ envName: e }));
      setEnvironmentList(uniqueEnvs);
    });
  }, []);

  // Propagate all state to parent whenever anything changes
  useEffect(() => {
    props.saveEventMonitorData({
      eventType,
      fileName,
      matchType,
      dbConnection,
      sql: sqlQuery,
      environmentName,
      pollIntervalSeconds: pollInterval,
      pollTimeoutSeconds: pollTimeout,
    });
  }, [
    eventType,
    fileName,
    matchType,
    dbConnection,
    sqlQuery,
    environmentName,
    pollInterval,
    pollTimeout,
  ]);

  const onChangeEventType = (value) => {
    setEventType(value);
    // Reset fields that don't apply to the other type
    if (value === "SQL Event") {
      setMatchType("");
      setFileName("");
    } else {
      setSqlQuery("");
      setDbConnection("");
      setEnvironmentName("");
    }
  };

  const onChangeEnvironment = (value) => {
    setEnvironmentName(value);
    setDbList(allConnections.filter((i) => i.environmentName === value));
    setDbConnection("");
  };

  // ── Poll config (shared by both event types) ──────────────
  const pollConfig = (
    <div
      style={{
        marginTop: 16,
        padding: "12px 16px",
        background: "var(--color-background-secondary)",
        borderRadius: 8,
        border: "0.5px solid var(--color-border-tertiary)",
      }}
    >
      <p
        style={{
          fontSize: 12,
          fontWeight: 500,
          marginBottom: 12,
          color: "var(--color-text-secondary)",
        }}
      >
        Poll configuration
      </p>
      <Row style={{ marginBottom: 8 }}>
        <Col span={10}>
          <p className="spec-modal-label" style={{ marginBottom: 0 }}>
            Poll every (seconds) *{" "}
            <Tooltip title="How often ARC checks for the event. Minimum 5 seconds.">
              <QuestionCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
            </Tooltip>
          </p>
        </Col>
        <Col span={14}>
          <InputNumber
            min={5}
            max={3600}
            value={pollInterval}
            onChange={(v) => setPollInterval(v || 10)}
            style={{ width: "100%" }}
            placeholder="10"
          />
        </Col>
      </Row>
      <Row>
        <Col span={10}>
          <p className="spec-modal-label" style={{ marginBottom: 0 }}>
            Timeout (seconds) *{" "}
            <Tooltip title="Give up and mark step as failed after this many seconds. Default 300 = 5 minutes.">
              <QuestionCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
            </Tooltip>
          </p>
        </Col>
        <Col span={14}>
          <InputNumber
            min={10}
            max={86400}
            value={pollTimeout}
            onChange={(v) => setPollTimeout(v || 300)}
            style={{ width: "100%" }}
            placeholder="300"
          />
        </Col>
      </Row>
    </div>
  );

  // ── File received in a location ───────────────────────────
  const renderFileEvent = () => (
    <div>
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p className="spec-modal-label">
            Match Filename *{" "}
            <Tooltip title="How to match the file name. 'Any' polls for any file in the folder.">
              <QuestionCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
            </Tooltip>
          </p>
        </Col>
        <Col span={6}>
          <Select
            placeholder="Match type"
            onChange={(e) => {
              if (e === "any") {
                setFileName("");
                setMatchDisabled(true);
              } else {
                setMatchDisabled(false);
              }
              setMatchType(e);
            }}
            style={{ width: "90%" }}
          >
            <Option value="equals">Equals</Option>
            <Option value="contains">Contains</Option>
            <Option value="any">Any file</Option>
          </Select>
        </Col>
        <Col span={12}>
          <Input
            value={fileName}
            disabled={matchDisabled}
            placeholder={matchDisabled ? "(any file)" : "file name or pattern"}
            onChange={(e) => setFileName(e.target.value)}
          />
        </Col>
      </Row>
      {pollConfig}
    </div>
  );

  // ── SQL Event ─────────────────────────────────────────────
  const renderSqlEvent = () => (
    <div>
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p className="spec-modal-label">Select Environment *</p>
        </Col>
        <Col span={18}>
          <DropdownSelect
            labelKey="envName"
            valueKey="envName"
            placeholder="Select environment"
            options={environmentList}
            onSelect={onChangeEnvironment}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p className="spec-modal-label">Database Connection *</p>
        </Col>
        <Col span={18}>
          <DropdownSelect
            labelKey="displayName"
            valueKey="displayName"
            placeholder="Select database connection"
            options={dbList}
            onSelect={(v) => setDbConnection(v)}
          />
        </Col>
      </Row>
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p className="spec-modal-label">
            SQL Query *{" "}
            <Tooltip title="Write a SELECT query. ARC polls this query until it returns at least one row. Use WHERE clauses to target the specific event you're waiting for.">
              <QuestionCircleOutlined style={{ color: "rgba(0,0,0,.45)" }} />
            </Tooltip>
          </p>
        </Col>
        <Col span={18}>
          <TextArea
            value={sqlQuery}
            onChange={(e) => setSqlQuery(e.target.value)}
            rows={5}
            placeholder={
              "Example:\nSELECT * FROM batch_jobs\nWHERE status = 'SUCCESS'\nAND job_name = 'EOD_RECON'\nAND run_date = CURRENT_DATE"
            }
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              background: "#0B1F3A",
              color: "#86EFAC",
              border: "1px solid #1e3a5f",
              borderRadius: 6,
            }}
          />
          <p
            style={{
              fontSize: 11,
              color: "var(--color-text-secondary)",
              marginTop: 4,
            }}
          >
            <CodeOutlined style={{ marginRight: 4 }} />
            Only SELECT statements are allowed. ARC polls until at least one row
            is returned.
          </p>
        </Col>
      </Row>
      {pollConfig}
    </div>
  );

  return (
    <div>
      <Row style={{ marginBottom: 12 }}>
        <Col span={6}>
          <p className="spec-modal-label">Event Type *</p>
        </Col>
        <Col span={18}>
          <DropdownSelect
            placeholder="Select Event Type"
            options={eventTypeList}
            labelKey="label"
            valueKey="value"
            onSelect={onChangeEventType}
          />
        </Col>
      </Row>
      {eventType === "File received in a location" && renderFileEvent()}
      {eventType === "SQL Event" && renderSqlEvent()}
    </div>
  );
}

export default EventMonitorTask;
